import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../Common/Configurations/AuthContext";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";
import Header from "../../Common/Layout/Header";
import {
  RiArrowLeftLine,
  RiPlaneLine,
  RiUserLine,
  RiCalendarLine,
  RiBuildingLine,
  RiFlightTakeoffLine,
  RiFlightLandLine,
  RiMoneyDollarCircleLine,
  RiCheckboxCircleLine,
  RiAlertLine,
  RiTimerLine,
  RiShieldLine,
  RiArrowRightLine
} from "react-icons/ri";

const SeriesFareBlockConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { fare, searchQuery, bookingPax } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!fare || !bookingPax) {
      navigate("/seriesfare");
    }
  }, [fare, bookingPax, navigate]);

  if (!fare || !bookingPax) return null;

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return `INR ${Math.round(num).toLocaleString("en-IN")}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  const calculateTotal = () => {
    if (!fare || !bookingPax) return 0;
    const pricePerPerson = parseFloat(fare.pricePerPerson) || 0;
    return pricePerPerson * bookingPax;
  };

  const handleBlockSeats = async () => {
    try {
      setLoading(true);
      setError(null);

      const createBookingDto = {
        seriesFareId: fare.id, // This should be the MongoDB ObjectId string
        pax: bookingPax,
        agentId: user?.cid
      };

      console.log("Creating block request:", createBookingDto);

      const response = await apiService.post(
        config.SERIESBOOKINGCREATE,
        createBookingDto
      );

      console.log("Block response:", response);

      if (response && response.success) {
        // Navigate to booking list with success message
        navigate("/seriesfare/agentbookings", {
          state: {
            message: {
              type: "success",
              text: `Block request submitted successfully! Booking ID: ${response.bookingId}`
            }
          }
        });
      } else {
        throw new Error(response?.message || "Failed to create block request");
      }
    } catch (error) {
      console.error("Block error:", error);
      setError(error.message || "Failed to block seats. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => navigate(-1);

  return (
    <div id="layout-wrapper">
      <Header />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="row justify-content-center">
              <div className="col-lg-8">
                {/* Header */}
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <button
                          className="btn btn-outline-secondary mb-3"
                          onClick={handleBack}
                          disabled={loading}
                        >
                          <RiArrowLeftLine className="me-2" />
                          Back to Results
                        </button>
                        <h3>Confirm Block Request</h3>
                        <p className="text-muted">
                          Review details and confirm your seat block request
                        </p>
                      </div>
                      <div className="text-end">
                        <span className="badge bg-warning fs-6 p-2">
                          <RiTimerLine className="me-2" />
                          BLOCK REQUEST
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Error Alert */}
                {error && (
                  <div className="alert alert-danger">
                    <RiAlertLine className="me-2" />
                    {error}
                  </div>
                )}

                {/* Flight Details Card */}
                <div className="card mb-4 border-warning">
                  <div className="card-header bg-warning text-white">
                    <h5 className="mb-0">
                      <RiPlaneLine className="me-2" />
                      Flight Details
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <small className="text-muted">Route</small>
                          <h4 className="text-primary">
                            <RiFlightTakeoffLine className="me-2" />
                            {fare.from}
                            <RiArrowRightLine className="mx-3" />
                            <RiFlightLandLine className="me-2" />
                            {fare.to}
                          </h4>
                        </div>
                        <div className="mb-3">
                          <small className="text-muted">Airline</small>
                          <h5>
                            <span className="badge bg-info fs-6">
                              {fare.airlineCode}
                            </span>
                          </h5>
                        </div>
                        <div className="mb-3">
                          <small className="text-muted">Departure Date</small>
                          <p className="fw-bold">
                            <RiCalendarLine className="me-2" />
                            {formatDate(fare.departureDate)}
                          </p>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <small className="text-muted">Cabin Class</small>
                          <p className="mb-2">
                            <span className="badge bg-primary fs-6">
                              {fare.cabinClass || "ECONOMY"}
                            </span>
                          </p>
                        </div>
                        <div className="mb-3">
                          <small className="text-muted">Validity Period</small>
                          <p className="mb-0">
                            {formatDate(fare.validFrom)} to {formatDate(fare.validTo)}
                          </p>
                        </div>
                        <div className="mb-3">
                          <small className="text-muted">DMC Provider</small>
                          <p className="fw-bold">
                            <RiBuildingLine className="me-2" />
                            {fare.companyName || "Direct Supplier"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Booking Summary Card */}
                <div className="card mb-4">
                  <div className="card-header bg-light">
                    <h5 className="mb-0">
                      <RiUserLine className="me-2" />
                      Booking Summary
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <small className="text-muted">Number of Passengers</small>
                          <h2>
                            <RiUserLine className="me-2 text-primary" />
                            {bookingPax}
                          </h2>
                        </div>
                        <div className="mb-3">
                          <small className="text-muted">Price per Person</small>
                          <h4 className="text-success">
                            {formatCurrency(fare.pricePerPerson)}
                          </h4>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <small className="text-muted">Total Amount</small>
                          <h1 className="text-primary">
                            {formatCurrency(calculateTotal())}
                          </h1>
                          <small className="text-muted">
                            {bookingPax} passenger{bookingPax > 1 ? 's' : ''}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Important Information */}
                <div className="card mb-4 border-info">
                  <div className="card-header bg-info text-white">
                    <h5 className="mb-0">
                      <RiAlertLine className="me-2" />
                      Important Information
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="alert alert-info mb-0">
                      <h6>About Block Requests:</h6>
                      <ul className="mb-0">
                        <li>This is a <strong>Block Request</strong>, not a confirmed booking</li>
                        <li>Seats will be temporarily held for you</li>
                        <li>DMC must approve your request within 24 hours</li>
                        <li>You will receive a notification when your request is approved</li>
                        <li>Once approved, you can complete payment to confirm booking</li>
                        <li>If not approved within 24 hours, the request will be cancelled automatically</li>
                        <li>No payment is required at this stage</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="card">
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-8">
                        <div className="d-flex align-items-center">
                          <div className="me-3">
                            <RiShieldLine className="text-success" size={40} />
                          </div>
                          <div>
                            <h6 className="mb-1">Secure & Risk-Free</h6>
                            <p className="text-muted mb-0 small">
                              No payment required now. Only pay after DMC confirms your seats.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="d-grid gap-2">
                          <button
                            className="btn btn-warning btn-lg"
                            onClick={handleBlockSeats}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Submitting Request...
                              </>
                            ) : (
                              <>
                                <RiCheckboxCircleLine className="me-2" />
                                Confirm Block Request
                              </>
                            )}
                          </button>
                          <button
                            className="btn btn-outline-secondary"
                            onClick={handleBack}
                            disabled={loading}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeriesFareBlockConfirmation;